import { useQuery } from "@tanstack/react-query";

export const CROSSD_APR_QUERY_KEY = "crossd-apr";

/**
 * CROSSD 대시보드 overview API. prd 환경만 존재하므로 URL을 고정한다.
 * 응답의 `est_last_epoch_apy`는 비율값(1 == 100%, 0.05 == 5%).
 */
const CROSSD_OVERVIEW_URL =
  "https://crossd-dashboard-api.crossdefi.io/api/v1/overview";

interface CrossdOverviewResponse {
  code: number;
  message: string;
  data: {
    est_last_epoch_apy: string;
  };
}

/**
 * CROSSD APR(`est_last_epoch_apy`)를 백분율 숫자로 반환한다.
 * API가 prd 전용이라 env와 무관하게 항상 prd URL에서 fetch해 표기한다.
 */
export function useCrossdApr() {
  const { data, ...rest } = useQuery<number>({
    queryKey: [CROSSD_APR_QUERY_KEY],
    queryFn: async ({ signal }) => {
      const res = await fetch(CROSSD_OVERVIEW_URL, {
        cache: "no-store",
        signal,
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch CROSSD overview: ${res.status}`);
      }
      const json: CrossdOverviewResponse = await res.json();
      if (json.code !== 200) {
        throw new Error(`CROSSD overview API error: ${json.code}`);
      }
      const ratio = parseFloat(json.data.est_last_epoch_apy);
      if (!Number.isFinite(ratio)) {
        throw new Error("CROSSD overview returned a non-numeric APR");
      }
      return ratio * 100;
    },
    staleTime: 60_000,
  });

  return { aprPercent: data ?? null, ...rest };
}
